import { AsyncPipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  Inject,
  NgZone,
  OnInit,
  signal,
  viewChild,
  ViewChild,
} from "@angular/core";
import { NewUiButton, translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { Document, DocumentMode, StaticDataType } from "@ankaadia/graphql";
import { EmptystateComponent } from "@ankaadia/ui";
import { TranslocoService } from "@jsverse/transloco";
import { FieldArrayType, FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { isNil, pick } from "lodash";
import { ConfirmationService as OldConfirmationService } from "primeng-v17/api";
import { TabView as OldTabView, TabViewCloseEvent as OldTabViewCloseEvent } from "primeng-v17/tabview";
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from "primeng/accordion";
import { ConfirmationService, MenuItem, PrimeIcons } from "primeng/api";
import { Menu } from "primeng/menu";
import { Tag } from "primeng/tag";
import { Tooltip } from "primeng/tooltip";
import { debounceTime, distinctUntilChanged, map, merge, Observable, of, startWith, switchMap } from "rxjs";
import { environment } from "../../../environments/environment";
import { DocumentsService } from "../../features/documents/documents.service";
import { CandidateProfileDialogService } from "../../organization-specific/base-candidate-profile/candidate-profile-dialog.service";
import { NewUiFormElementMapModule } from "../../shared/from-element-map/new-ui-form-element-map.module";
import { hasFormErrors, isFilledByAI, isSuggested } from "../../shared/functions/form-errors";
import { outsideZone } from "../../shared/rxjs/outside-zone";
import { StaticDataService } from "../../shared/static-data/static-data.service";
import { TestIdDirective } from "../../shared/test-id/test-id.directive";

@Component({
  selector: "new-ui-formly-repeat-tabs",
  templateUrl: "./new-ui-formly-repeat-tabs.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormlyModule,
    TestIdDirective,
    AsyncPipe,
    NewUiFormElementMapModule,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    Tag,
    Menu,
    NewUiButton,
    Tooltip,
    EmptystateComponent,
  ],
})
export class NewUiFormlyRepeatTabsComponent extends FieldArrayType implements OnInit {
  private readonly language = this.transloco.getActiveLang();

  protected isDisabled: boolean;
  protected uploadMenu: MenuItem[];
  protected invalid$: Observable<boolean[]>;
  protected suggested$: Observable<boolean[]>;
  protected filledByAI$: Observable<boolean[]>;
  protected hoveredRemoveIndex = -1;

  @ViewChild(OldTabView)
  tabView: OldTabView;

  accordion = viewChild(Accordion);
  openAccordionIndex = signal<number | null>(null);

  // Effect to open an accordion panel when the user add a new item
  // This workaround is needed because the Accordion is not instantiated if there is no panel...
  openAccordionPanel = effect(() => {
    if (!environment.newUI) return;

    const index = this.openAccordionIndex();
    const accordion = this.accordion();

    if (isNil(index) || isNil(accordion)) return;

    const accordionValue = accordion?.value();

    if (accordionValue === index) return;
    if (Array.isArray(accordionValue) && accordionValue?.some((i) => i === index)) return;

    accordion.updateValue(index);
    this.openAccordionIndex.set(null);
  });


  index = this.openAccordionIndex();

  get isAddEnabled(): boolean {
    return (
      !this.props.readonly &&
      this.formControl.enabled &&
      (isNil(this.props.maxCount) || this.props.maxCount > this.field.fieldGroup.length) &&
      !this.props.disabledAddButton
    );
  }

  get isAdditionalEnabled(): boolean {
    return (
      !this.props.readonly &&
      this.formControl.enabled &&
      (isNil(this.props.maxCount) || this.props.maxCount > this.field.fieldGroup.length) &&
      !this.props.disableAdditionalAddButton
    );
  }

  isOpened(accordionIndex: number): boolean {
    if (!environment.newUI) return null;

    const openedIndex = this.accordion()?.value();
    if (Array.isArray(openedIndex)) {
      return openedIndex.some((i) => i === accordionIndex);
    }
    return openedIndex === accordionIndex;
  }

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly staticDataService: StaticDataService,
    private readonly transloco: TranslocoService,
    private readonly documentService: DocumentsService,
    private readonly profileDialogService: CandidateProfileDialogService,
    @Inject(environment.newUI ? ConfirmationService : OldConfirmationService)
    private readonly confirmationService: ConfirmationService | OldConfirmationService,
    private readonly ngZone: NgZone
  ) {
    super();
  }

  ngOnInit(): void {
    this.invalid$ = this.getInvalid();
    this.suggested$ = this.getSuggested();
    this.filledByAI$ = this.getFilledByAI();
    this.field.formControl.statusChanges
      .pipe(debounceTime(200), outsideZone(this.ngZone))
      .subscribe(() => this.statusChanged());
    this.field.formControl.valueChanges
      .pipe(debounceTime(100), outsideZone(this.ngZone))
      .subscribe(() => this.valueChanged());
    this.setUploadMenu();
  }

  handleAdd(event: Event, value: any): void {
    this.add(null, value);
    this.changeDetector.detectChanges();
    if (environment.newUI) {
      const accordionValue = this.field.fieldGroup.length - 1;
      this.openAccordionIndex.set(accordionValue);
    } else {
      this.tabView?.open(event, this.tabView.tabs[this.tabView.tabs.length - 1]);
    }
  }

  protected handleRemove(index: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.remove(index);
  }

  handleClose(event: OldTabViewCloseEvent): void {
    const accept = (): void => {
      this.remove(event.index);
      event.close();
      this.tabView?.open(event.originalEvent, this.tabView.tabs[0]);
    };

    this.confirmationService.confirm({
      target: event.originalEvent.target,
      message: translateSafely("addresses.confirmDelete"),
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      acceptButtonProps: { severity: "danger" },
      accept: accept,
    });
  }

  isRemoveEnabled(i: number): boolean {
    return (
      !this.props.readonly &&
      this.formControl.enabled &&
      (isNil(this.props.minCount) || i >= this.props.minCount) &&
      !this.props.disabledRemoveButton
    );
  }

  protected openUploadDialog(documentType: string): void {
    this.documentService
      .getByType(documentType, this.formState.organizationId, this.formState.candidateId)
      .pipe(
        switchMap((document) => (document ? of(document) : this.createDocument(documentType))),
        map((document) => ({
          document,
          candidateForm: this.form,
          showDocumentCompletion: this.props.showDocumentCompletion,
        }))
      )
      .subscribe((event) => this.profileDialogService.documentDialog.next(event));
  }

  private createDocument(type: string): Observable<Document> {
    return this.documentService.createEmptyDocument(
      DocumentMode.Candidate,
      this.formState.organizationId,
      this.formState.candidateId,
      null,
      type
    );
  }

  private valueChanged(): void {
    this.tabView?.updateInkBar();
  }

  private statusChanged(): void {
    // Always trigger change detection so isAddEnabled re-evaluates formControl.enabled,
    // even when fieldGroup is empty (e.g. no entries yet when checkbox disables the list).
    this.changeDetector.detectChanges();
    this.field.fieldGroup.forEach(() => {
      this.isDisabled = !this.isDisabled;
      this.changeDetector.detectChanges();
      this.isDisabled = !this.isDisabled;
      this.changeDetector.detectChanges();
      this.tabView?.updateInkBar();
    });
  }

  private setUploadMenu(): void {
    if (!this.props.uploadTypes) return;

    if (this.formState.candidateId) {
      this.staticDataService
        .getStaticData(
          StaticDataType.AllowedUploadFileTypes,
          this.language,
          pick(this.formState, "candidateId", "organizationId")
        )
        .subscribe((data) => {
          this.uploadMenu = this.props.uploadTypes?.map((documentType) => ({
            label: data.find((d) => d.value == documentType)?.label,
            command: (): void => this.openUploadDialog(documentType),
          }));
          this.changeDetector.detectChanges();
        });
    } else {
      this.uploadMenu = null;
    }
  }

  private getInvalid(): Observable<boolean[]> {
    return merge(this.options.fieldChanges, this.form.statusChanges).pipe(
      startWith(undefined),
      debounceTime(100),
      map(() => this.calculateInvalidity()),
      distinctUntilChanged(),
      outsideZone(this.ngZone)
    );
  }

  private calculateInvalidity(): boolean[] {
    const func = (field: FormlyFieldConfig): boolean =>
      field.fieldGroup?.length && !field.fieldArray
        ? field.fieldGroup.some((f) => func(f))
        : hasFormErrors(field.formControl);

    return this.field.fieldGroup.map((field) => func(field));
  }

  private getSuggested(): Observable<boolean[]> {
    return merge(this.options.fieldChanges, this.form.statusChanges).pipe(
      startWith(undefined),
      debounceTime(100),
      map(() => this.calculateSuggested()),
      distinctUntilChanged(),
      outsideZone(this.ngZone)
    );
  }

  private calculateSuggested(): boolean[] {
    const func = (field: FormlyFieldConfig): boolean =>
      field.fieldGroup?.length && !field.fieldArray
        ? field.fieldGroup.some((f) => func(f))
        : isSuggested(field.formControl);

    return this.field.fieldGroup.map((field) => func(field));
  }

  private getFilledByAI(): Observable<boolean[]> {
    return merge(this.options.fieldChanges, this.form.statusChanges).pipe(
      startWith(undefined),
      debounceTime(100),
      map(() => this.calculateFilledByAI()),
      distinctUntilChanged(),
      outsideZone(this.ngZone)
    );
  }

  private calculateFilledByAI(): boolean[] {
    const invalid = this.calculateInvalidity();
    const func = (field: FormlyFieldConfig): boolean =>
      field.fieldGroup?.length && !field.fieldArray
        ? field.fieldGroup.some((f) => func(f))
        : isFilledByAI(field.formControl);

    return this.field.fieldGroup.map((field, i) => !invalid[i] && func(field));
  }
}
